import { format, isToday, isYesterday } from "date-fns";
import type { ActivityLog } from "@/services/activityLogService";

const formatActionLabel = (action: string): string =>
  action
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const toNormalized = (value: string | null | undefined): string =>
  value?.toLowerCase() ?? "";

export const formatAction = (
  action: string,
  service?: ActivityLog["service"],
): string => {
  const normalizedAction = toNormalized(action);
  const normalizedService = toNormalized(service);

  if (
    normalizedService === "bookingcheckinservice" &&
    normalizedAction === "checkin_completed"
  ) {
    return "Pre Check-in Completed";
  }

  if (
    normalizedService === "bookingcheckoutservice" &&
    normalizedAction === "checkout_completed"
  ) {
    return "Pre Check-out Completed";
  }

  return formatActionLabel(action);
};

export const formatTime = (iso: string): string => {
  const d = new Date(iso);
  if (isToday(d)) return format(d, "h:mm a");
  if (isYesterday(d)) return `Yesterday ${format(d, "h:mm a")}`;
  return format(d, "MMM d, h:mm a");
};

const toNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const formatInitialLast = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return fullName;
  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first.charAt(0).toUpperCase()}. ${last}`;
};

export const formatStaffName = (
  staffName?: ActivityLog["activity"]["staffName"],
): string => {
  if (typeof staffName === "string") {
    const v = toNonEmptyString(staffName);
    return v ? formatInitialLast(v) : "";
  }

  if (!staffName || typeof staffName !== "object") {
    return "";
  }

  const staff = staffName as Record<string, unknown>;
  const profile = staff.profileId;

  if (profile && typeof profile === "object") {
    const profileRecord = profile as Record<string, unknown>;

    const first =
      toNonEmptyString(profileRecord.firstName) ??
      toNonEmptyString(profileRecord.first_name) ??
      toNonEmptyString(profileRecord.first);
    const last =
      toNonEmptyString(profileRecord.lastName) ??
      toNonEmptyString(profileRecord.last_name) ??
      toNonEmptyString(profileRecord.last);

    if (first && last) {
      return `${first.charAt(0).toUpperCase()}. ${last}`;
    }

    const full =
      toNonEmptyString(profileRecord.fullName) ??
      toNonEmptyString(profileRecord.name) ??
      toNonEmptyString(profileRecord.displayName);

    if (full) {
      return formatInitialLast(full);
    }
  }

  const profileIdString = toNonEmptyString(staff.profileId);
  if (profileIdString) return formatInitialLast(profileIdString);

  return toNonEmptyString(staff.id) || "";
};

export const formatActivityActorName = (log: ActivityLog): string => {
  const staffLabel = formatStaffName(log.activity.staffName);
  if (staffLabel) return staffLabel;

  const guestUsername = toNonEmptyString(log.activity.guestUsername);
  if (guestUsername) return guestUsername;

  const username = toNonEmptyString(log.activity.username);
  if (username) return username;

  const participantLabel =
    typeof log.activity.participantLabel === "string"
      ? toNonEmptyString(log.activity.participantLabel)
      : null;

  return participantLabel ?? "";
};

export const formatActivityTitle = (log: ActivityLog): string => {
  const normalizedAction = toNormalized(log.activity.action);
  const normalizedService = toNormalized(log.service);
  const guestUsername = toNonEmptyString(log.activity.guestUsername);
  const username = toNonEmptyString(log.activity.username);
  const activityText = toNonEmptyString(log.activity.text);
  const rating =
    typeof log.activity.rating === "number" ? log.activity.rating : null;

  if (normalizedAction === "checkout_feedback_created") {
    const actor = username ?? guestUsername ?? "Guest";
    const ratingLabel =
      rating === null ? "feedback" : `feedback: ${rating} stars`;
    return `${actor} left ${ratingLabel}`;
  }

  if (activityText) {
    return activityText;
  }

  if (
    normalizedService === "bookingcheckinservice" &&
    normalizedAction === "checkin_completed"
  ) {
    return guestUsername
      ? `Guest ${guestUsername} completed pre check-in`
      : "Guest completed pre check-in";
  }

  if (
    normalizedService === "bookingcheckoutservice" &&
    normalizedAction === "checkout_completed"
  ) {
    return guestUsername
      ? `Guest ${guestUsername} completed pre check-out`
      : "Guest completed pre check-out";
  }

  return formatAction(log.activity.action, log.service);
};

export const formatReservationLogMessage = (log: ActivityLog): string => {
  const actionLabel = formatActivityTitle(log);
  const statusPart = log.activity.status ? ` — ${log.activity.status}` : "";
  const roomPart = log.activity.roomNumber
    ? ` (${log.activity.roomNumber})`
    : "";
  return `${actionLabel}${statusPart}${roomPart}`;
};
